<template>
    <ul
        class="ds-social-bar"
        :class="`ds-social-bar--${theme} ds-social-bar--${disposition}`"
        role="menubar"
        data-j-socialbar-list>
        <li
            v-for="(item, index) in items"
            :key="`social-bar-item-${index}`"
            class="ds-social-bar__list-item"
            role="none">
            <DsLink
                :content="{ category: 'extra', name: item.iconName, ariaLabel: item.text }"
                :url="item.url"
                open-in-new-window
                role="menuitem"
                class="ds-social-bar__link"
                type="button"
                data-j-socialbar-list-link
                @link-clicked="(e) => $emit('link-clicked', {...e, label: item.text })" />
        </li>
    </ul>
</template>

<script>
import DsLink from '../link/link';
import { theme } from '../../utils/mixins';

/**
 * Social bar component.
 * @mixins theme
 *
 * @emits link-clicked
 *
 * Accessibility
 * @see {@link https://www.w3.org/TR/2021/NOTE-wai-aria-practices-1.2-20211129/examples/menubar/menubar-navigation.html}
 *
 * @example
 * import { DsSocialBar } from '@/components/index'
 *
 * export default {
 *   components: { DsSocialBar },
 *   template: `<div>
 *     <DsSocialBar
 *         :theme="light or dark"
 *         :options="[{ iconName: 'a', text: 'a', url: '#' }]"
 *     />
 *  `</div>
 */

/**
 * Link Clicked event
 * @event link-clicked
 * @type {Object}
 * @property {String} url - link url
 * @property {String} label - link text
 */

export default {
    name: 'SocialBar',
    components: { DsLink },
    mixins: [theme],
    props: {
        /**
         * SocialBar items
         */
        items: {
            type: Array,
            required: true
        },
        /**
         * SocialBar items position
         */
        disposition: {
            type: String,
            default: 'vertical',
            validator: disposition => ['vertical', 'horizontal'].includes(disposition)
        }
    }
};
</script>

<style lang="scss" scoped>
@import '../../foundation/tokens/variables';
@import '../../foundation/mixins/buttons/button-states.mixins';

.ds-social-bar {
    $this: &;

    display: flex;

    &__link {
        :deep(.icon) {
            height: $spacing-24;
            width: $spacing-24;
        }
    }

    &--light {
        #{$this}__link {
            @include button-states(
                $color-black, // text-color
                $color-black, // text-hover-color
                $color-white, // background-color
                $color-grey-050, // background-hover-color
                $color-grey-100, // border-color
                $color-grey-350 // border-focus-color
            );
        }
    }

    &--dark {
        #{$this}__link {
            @include button-states(
                $color-grey-350, // text-color
                $color-black, // text-hover-color
                transparent, // background-color
                $color-grey-100, // background-hover-color
                $color-grey-600, // border-color
                $color-green-200 // border-focus-color
            );
        }
    }

    &--vertical {
        flex-direction: column;

        #{$this}__list-item {
            margin-top: $spacing-12;

            &:first-child {
                margin-top: 0;
            }
        }
    }

    &--horizontal {
        flex-direction: row;

        #{$this}__list-item {
            margin-left: $spacing-12;

            &:first-child {
                margin-left: 0;
            }
        }
    }
}
</style>
